import { useDroppable } from "@dnd-kit/core";
import { Archive, ChevronDown } from "lucide-react";
import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useArchivedChatList } from "@/api/chat/useArchivedChatList";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useSidebarSectionCollapse } from "@/hooks/use-sidebar-section-collapse";
import { cn } from "@/lib/utils";

import { useSidebarInfiniteScroll } from "../../hooks/use-sidebar-infinite-scroll";
import { ArchivedChatsSkeleton } from "./ArchivedChatsSkeleton";
import { ChatListItem } from "./ChatListItem";
import { SidebarGroupCount } from "./SidebarGroupCount";

const ARCHIVED_CHATS_SECTION_KEY = "archived" as const;

/**
 * NOTE: NSAI-6342 — the archive is docked to the foot of the chats pane rather
 * than sitting in the scrolling list with it. It is not a tab of its own: an
 * archive is a state of the chats, not a fourth thing beside them, and giving
 * it a tab would have spent a third of the switch on the list people open
 * least. But left in the flow it fell below however many chats there were and
 * read as detached from the group above it, so it is pinned instead: one row
 * above the account block, always in reach whatever the list has grown to, and
 * always the same distance from the bottom of the sidebar.
 *
 * Pinning it is also what makes it a reliable drag target — archiving a chat is
 * a drag onto this row, and a drop zone that scrolls out of the pane cannot be
 * dropped on without dragging the list at the same time.
 *
 * Open, the list grows upward out of the row into at most 40% of the viewport
 * and scrolls inside that, so the archive can never push the account block off
 * screen or take the pane over.
 */
const ArchivedChatsInner = (): React.ReactElement | null => {
  const { t } = useTranslation("sidebar");

  const { ref, isInView } = useSidebarInfiniteScroll();

  // NOTE: NSAI-5926 — the archive still ships closed, but once the user opens
  // it that choice is remembered, and so is closing it again. The persisted
  // record distinguishes "never touched" from "explicitly closed", which is
  // what lets a default-closed section persist alongside default-open ones.
  const { openSectionKeys, handleOpenSectionsChange } =
    useSidebarSectionCollapse({
      sectionKey: ARCHIVED_CHATS_SECTION_KEY,
      isCollapsedByDefault: true,
    });

  const { setNodeRef, isOver } = useDroppable({
    id: "archive-drop-zone",
    data: { type: "archive" },
  });

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useArchivedChatList({
      // NOTE: Always fetch to check if there are any archived chats
      enabled: true,
    });

  // NOTE: Infinite scroll for archived chats
  useEffect(() => {
    if (isInView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const archivedChats = data?.pages.flatMap((page) => page.data) ?? [];

  // NOTE: Hide the section entirely if no archived chats exist
  if (archivedChats.length === 0) {
    return null;
  }

  const isShowingLoader = isLoading && archivedChats.length === 0;

  return (
    <div
      ref={setNodeRef}
      data-testid="archive-drop-zone"
      className={cn(
        // NOTE: the hairline is what separates the docked row from the list
        // scrolling above it; without it the row looks like the list's last
        // entry rather than a fixed part of the pane.
        "shrink-0 border-t border-sidebar-border px-2.5 py-1",
        "group-data-[collapsible=icon]:hidden",
        isOver && "bg-sidebar-accent/60",
      )}
    >
      <Accordion
        type="multiple"
        value={openSectionKeys}
        onValueChange={handleOpenSectionsChange}
      >
        <AccordionItem
          value={ARCHIVED_CHATS_SECTION_KEY}
          className="border-b-0"
        >
          <AccordionTrigger
            // NOTE: the chevron is positioned against the trailing edge, so
            // the row reserves that width — without it the count sits under
            // the glyph.
            className="relative gap-2 rounded-lg ps-0 pe-7 py-1.5 text-sm font-medium text-foreground hover:no-underline justify-between"
            data-testid="archived-chats-trigger"
            chevronElement={
              <span className="absolute top-1/2 -translate-y-1/2 ltr:right-0 rtl:left-0 h-6 w-7 flex items-center justify-center">
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/accordion:rotate-180" />
              </span>
            }
          >
            <Archive className="h-4 w-4 shrink-0 text-foreground-secondary" />
            <span className="flex-1 truncate text-start">
              {t("chat_list.groups.archived")}
            </span>
            {/* NOTE: NSAI-6342 — the count is what a collapsed row has to say
                for itself; the same bare numeral every group in the pane
                carries, partial while the archive has more pages to fetch. */}
            <SidebarGroupCount
              count={archivedChats.length}
              isPartial={hasNextPage}
            />
          </AccordionTrigger>
          <AccordionContent className="-mx-2.5 max-h-[40vh] space-y-0.5 overflow-y-auto pb-0 themed-scrollbar">
            {isShowingLoader ? (
              <ArchivedChatsSkeleton />
            ) : (
              <>
                {archivedChats.map((chat) => (
                  <ChatListItem key={chat.uuid} chat={chat} />
                ))}
                <div ref={ref}>
                  {isFetchingNextPage && <ArchivedChatsSkeleton />}
                </div>
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export const ArchivedChats = memo(ArchivedChatsInner);
